import React, { useEffect, useRef } from "react";
import { Container, Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiX, FiSend, FiDownload } from "react-icons/fi";

import FinanceStatisticsView from "../components/FinanceStatisticsView";
import FinanceStatisticsTabs, { type FinanceTabKey } from "../components/FinanceStatisticsTabs";
import { ChurchIncomeBreakdownTable, ChurchDistrictInHouseTable } from "../components/ChurchIncomeTables";
import {
  fetchFinanceStatisticsAsync,
  fetchFinanceCompositeAsync,
  type ChurchFinanceStatisticsReport,
  type ChurchIncomeCompositeReport,
} from "@core/reports/api/fetchFinanceReport";
import type { ReportProcessResult, DepartmentReportMediaItem, DepartmentReportDocument } from "@core/reports/api/types";

import { requestReportApproveAsync } from "@core/reports/api/requestReportApprove";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import RequestReviewModal, {
  type RequestReviewConfirmPayload,
} from "@core/reports/components/RequestReviewModal";
import { requestDepartmentReportSaveAsync } from "@core/reports/api/requestDepartmentReportSave";
import { requestDepartmentReportSubmitAsync } from "@core/reports/api/requestDepartmentReportSubmit";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import ReportActionsPanel from "@core/components/ReportActionsPanel";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { financeStatsListToViewTrail } from "@core/navigation/pageBreadcrumbTrails";
import NoDepartmentReportNotice from "@core/reports/components/NoDepartmentReportNotice";
import { useDepartmentReportMedia } from "@core/reports/hooks/useDepartmentReportMedia";
import DepartmentReportMediaPanel from "@core/reports/components/DepartmentReportMediaPanel";
import {
  canApproveDepartmentReport,
  canRequestDepartmentReportReview,
  canDownloadReport,
  getApprovalNotes,
  getNeedsAttentionReason,
  parseNeedsAttentionDetails,
  getRequestReviewCopy,
  getWorkflowStatusLabel,
  isDepartmentReportEditable,
  resolveDepartmentReportDisplayStatus,
  type ReportProcessResults,
} from "@core/reports/utils/reportWorkflow";
import { useLeadershipReportPermissions } from "@core/reports/hooks/useLeadershipReportPermissions";
import { useSelectedReportTag } from "@core/reports/hooks/useSelectedReportTag";
import { surfaceDeptActionError } from "@core/reports/utils/surfaceDeptActionError";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { requestActivityEntryReviewAsync } from "@features/report/activity-entry/api/activityEntryApi";
import { useAuthStore } from "@features/auth/store/authStore";

export default function ChurchFinanceStatisticsPage() {
  const { churchId: urlChurchId } = useParams<{ churchId: string }>();
  const [searchParams] = useSearchParams();
  const numericChurchId = Number(urlChurchId) || 0;

  const { departmentId, reportTagId } = parseReportViewSearch(searchParams);

  // Tab 3 (statistics) — loaded on mount.
  const [stats, setStats] = React.useState<ChurchFinanceStatisticsReport | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Tabs 1 & 2 (income composite) — lazy-loaded on first open.
  const [composite, setComposite] = React.useState<ChurchIncomeCompositeReport | null>(null);
  const [compositeLoading, setCompositeLoading] = React.useState(false);
  const [compositeError, setCompositeError] = React.useState<string | null>(null);
  const compositeRequested = useRef(false);

  const [deptNotes, setDeptNotes] = React.useState("");
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [submittingDept, setSubmittingDept] = React.useState(false);
  const [validatingDept, setValidatingDept] = React.useState(false);
  const [deptActionError, setDeptActionError] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<ReportProcessResult | null>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  const [showApproveModal, setShowApproveModal] = React.useState(false);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [reviewMode, setReviewMode] = React.useState<"department" | "activity">("department");
  const [selectedReviewTemplate, setSelectedReviewTemplate] = React.useState<{
    template_id: number;
    template_name: string;
    submitted_by?: number | null;
  } | null>(null);

  const canLoad = numericChurchId > 0 && reportTagId > 0;

  const load = React.useCallback(async (): Promise<void> => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFinanceStatisticsAsync<ChurchFinanceStatisticsReport>("church", numericChurchId, reportTagId);
      if (result.isSuccess()) {
        setStats(result.getData());
      } else {
        setError(result.getError()?.detail || "Failed to fetch Finance & Statistics report");
        setStats(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [numericChurchId, reportTagId, canLoad]);

  React.useEffect(() => {
    if (canLoad) load();
  }, [load, canLoad]);

  const loadComposite = React.useCallback(async (): Promise<void> => {
    if (!canLoad || compositeRequested.current) return;
    compositeRequested.current = true;
    setCompositeLoading(true);
    setCompositeError(null);
    try {
      const result = await fetchFinanceCompositeAsync<ChurchIncomeCompositeReport>("church", numericChurchId, reportTagId);
      if (result.isSuccess()) setComposite(result.getData());
      else setCompositeError(result.getError()?.detail || "Failed to fetch income breakdown");
    } catch (e) {
      setCompositeError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setCompositeLoading(false);
    }
  }, [numericChurchId, reportTagId, canLoad]);

  const handleTabOpen = (key: FinanceTabKey) => {
    if (key === "breakdown" || key === "inhouse") void loadComposite();
  };

  const meta = stats?.meta;
  const submission = meta?.department_submission ?? null;
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const requestReviewCopy = getRequestReviewCopy(
    currentUserId,
    submission?.submitted_by
  );
  const requestReviewLabel = requestReviewCopy.actionLabel;
  const requestReviewNoun = requestReviewCopy.noun;
  const departmentReportId: number | null = submission?.department_report_id ?? null;
  const statusName = resolveDepartmentReportDisplayStatus(
    departmentReportId,
    submission?.report_status_name ?? null
  );
  const processResults = (meta as { process_results?: ReportProcessResults | null } | undefined)
    ?.process_results;
  const needsAttentionReason = getNeedsAttentionReason(statusName, processResults);
  const needsAttentionDetails = parseNeedsAttentionDetails(processResults);
  const approvalNotesDisplay = getApprovalNotes(submission?.approval_notes, processResults);

  const { permissionFlags, actionsVisible, canReviewActivity, permissionsLoading } =
    useLeadershipReportPermissions({
      scope: "church",
      scopeId: numericChurchId,
      departmentId,
      statusName,
      submittedBy: submission?.submitted_by,
      enabled: canLoad,
      includeActivityEntry: true,
    });

  const { tagIsOpen, tagStatusName } = useSelectedReportTag({
    churchId: numericChurchId || undefined,
    reportTagId,
    enabled: canLoad,
  });

  const canEdit = permissionFlags.fieldsEditable && tagIsOpen;
  const { showSave, showSubmit, showValidate, showApprove, showReview, showUploadMedia } = permissionFlags;
  const showSaveAction = showSave && tagIsOpen;
  const showSubmitAction = showSubmit && tagIsOpen;
  const showUploadMediaAction = showUploadMedia && tagIsOpen;
  const canRemoveMedia = canEdit && permissionFlags.canRemoveMedia;

  const sourceTemplates = React.useMemo(() => {
    const fromMeta = meta?.source_templates ?? [];
    return fromMeta
      .map((t) => ({
        template_id: Number(t.template_id) || 0,
        template_name: String(t.template_name ?? "").trim(),
        submitted_by: t.submitted_by ?? null,
      }))
      .filter((t) => t.template_id > 0 && t.template_name !== "");
  }, [meta]);

  const existingMedia = React.useMemo<DepartmentReportMediaItem[]>(
    () => (Array.isArray(meta?.media) ? (meta?.media as DepartmentReportMediaItem[]) : []),
    [meta]
  );
  const mediaRequirements = React.useMemo<DepartmentReportDocument[]>(() => {
    const value = meta?.documents;
    return Array.isArray(value) ? value : [];
  }, [meta]);

  const media = useDepartmentReportMedia({
    scope: "church",
    entityId: numericChurchId,
    departmentId,
    reportTagId,
    notes: deptNotes,
    departmentReportId,
    existingMedia,
    editable: showUploadMediaAction,
    onChanged: async () => { await load(); },
  });

  React.useEffect(() => {
    if (savingDraft || submittingDept) return;
    setDeptNotes(String(submission?.notes ?? ""));
  }, [submission?.notes, submission, savingDraft, submittingDept]);

  React.useEffect(() => {
    setDeptActionError(null);
    setValidationResult(null);
    media.clearUploadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericChurchId, departmentId, reportTagId]);

  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [deptNotes]);

  const handleSaveDraft = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSavingDraft(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSaveAsync({
        scope: "church", churchId: numericChurchId, departmentId, reportTagId,
        notes: deptNotes, existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to save draft.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to save draft.",
        setDeptActionError
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSubmittingDept(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSubmitAsync({
        scope: "church", churchId: numericChurchId, departmentId, reportTagId,
        notes: deptNotes, existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to submit report.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to submit report.",
        setDeptActionError
      );
    } finally {
      setSubmittingDept(false);
    }
  };

  const handleValidateDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setValidatingDept(true);
    try {
      const result = await processDepartmentReportAsync({ scope: "church", churchId: numericChurchId, departmentId, reportTagId });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to validate report.");
      setValidationResult(result.getData());
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to validate report.",
        setDeptActionError
      );
    } finally {
      setValidatingDept(false);
    }
  };

  const handleRequestReview = async ({ reason }: RequestReviewConfirmPayload) => {
    if (!canLoad) return;

    setRequestingReview(true);
    setReviewError(null);
    try {
      if (reviewMode === "activity") {
        const resolvedTemplateId = Number(selectedReviewTemplate?.template_id) || 0;
        if (resolvedTemplateId <= 0) {
          setReviewError("A report template is required to request activity review.");
          return;
        }
        const result = await requestActivityEntryReviewAsync(
          numericChurchId,
          resolvedTemplateId,
          reportTagId,
          { review_notes: reason }
        );
        if (!result.isSuccess()) {
          throw new Error(result.getError()?.detail || "Failed to request review.");
        }
      } else {
        if (!departmentReportId) {
          setReviewError("Missing department report id. Cannot request review.");
          return;
        }
        const result = await requestReportReviewAsync({
          scope: "church",
          churchId: numericChurchId,
          departmentId,
          departmentReportId: Number(departmentReportId),
          notes: reason.slice(0, 255),
        });
        if (!result.isSuccess()) {
          throw new Error(result.getError()?.detail || "Failed to request review.");
        }
      }
      setShowReviewModal(false);
      await load();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingReview(false);
    }
  };

  const openDepartmentReviewModal = () => {
    setReviewMode("department");
    setSelectedReviewTemplate(null);
    setReviewError(null);
    setShowReviewModal(true);
  };

  const openActivityReviewModal = (template: {
    template_id: number;
    template_name: string;
    submitted_by?: number | null;
  }) => {
    setReviewMode("activity");
    setSelectedReviewTemplate(template);
    setReviewError(null);
    setShowReviewModal(true);
  };

  const handleApprove = async () => {
    if (!canLoad || !departmentReportId) {
      setApproveError("Missing department report id. Cannot approve report.");
      return;
    }
    setApproving(true);
    setApproveError(null);
    try {
      const result = await requestReportApproveAsync({
        scope: "church", churchId: numericChurchId, departmentId,
        departmentReportId: Number(departmentReportId), notes: approveNotes ?? "",
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to approve report.");
      setShowApproveModal(false);
      await load();
    } catch (e) {
      setApproveError(e instanceof Error ? e.message : "Failed to approve report.");
    } finally {
      setApproving(false);
    }
  };

  const [downloadingPdf, setDownloadingPdf] = React.useState(false);
  const handleDownloadPdf = async () => {
    if (!stats) return;
    setDownloadingPdf(true);
    try {
      const { exportFinanceStatisticsPdf } = await import("@core/reports/pdf/exportFinanceStatisticsPdf");
      await exportFinanceStatisticsPdf(stats, {
        scope: "church",
        statusName,
        notes: submission?.notes ?? deptNotes,
      });
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to generate PDF.",
        setDeptActionError
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const busy = savingDraft || submittingDept || validatingDept;
  const canApproveNow = canApproveDepartmentReport(statusName);
  const canRequestDepartmentReviewNow =
    actionsVisible && showReview && canRequestDepartmentReportReview(statusName) && !!departmentReportId;
  const canRequestActivityReviewNow =
    canReviewActivity &&
    !permissionsLoading &&
    isDepartmentReportEditable(statusName) &&
    sourceTemplates.length > 0;
  const canDownload = canDownloadReport(statusName);

  const renderStatistics = () => (
    <>
      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading Finance &amp; Statistics…</span>
        </div>
      )}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {stats && !loading && <FinanceStatisticsView data={stats} />}

      {meta && canRequestActivityReviewNow ? (
        <Card className="mt-4">
          <Card.Body>
            <div className="fw-semibold">Activity submissions</div>
            <div className="text-muted small mb-3">
              Return a submitted activity entry report for correction.
            </div>
            <div className="d-flex flex-column gap-2">
              {sourceTemplates.map((template) => (
                <div
                  key={template.template_id}
                  className="d-flex align-items-center justify-content-between gap-3"
                >
                  <span className="fw-medium">{template.template_name}</span>
                  <Button
                    variant="outline-primary"
                    className="btn-action"
                    onClick={() => openActivityReviewModal(template)}
                    disabled={busy || !canLoad || requestingReview || approving}
                    title={`Request review for ${template.template_name}`}
                  >
                    <FiSend className="me-2" aria-hidden />
                    Request review
                  </Button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {meta && (
        <Card className="mt-4">
          <Card.Body>
            <div className="fw-semibold">Department Report Notes</div>
            <div className="text-muted small mb-3">
              {canEdit
                ? `Notes are editable while the report is in ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}. (KPI values are entered under Activity Submission.)`
                : "Notes are read-only once the report has been submitted for review or approval."}
            </div>
            <Form.Group className="mb-0">
              <Form.Control
                ref={notesRef as React.Ref<HTMLTextAreaElement>}
                as="textarea"
                value={deptNotes}
                onChange={(e) => setDeptNotes(e.target.value)}
                disabled={!canEdit || busy}
                placeholder={canEdit ? "Enter notes for this report..." : `Notes can only be edited when the report is ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`}
                style={{ minHeight: "6rem" }}
              />
            </Form.Group>

            <DepartmentReportMediaPanel
              requirements={mediaRequirements}
              existingMedia={existingMedia}
              captionDrafts={media.captionDrafts}
              mediaActionId={media.mediaActionId}
              captionSavingId={media.captionSavingId}
              error={media.error}
              editable={showUploadMediaAction}
              allowRemove={canRemoveMedia}
              busy={busy}
              uploadBusy={media.uploadBusy}
              uploadDraft={media.uploadDraft}
              uploadError={media.uploadError}
              namedFileInputRefs={media.namedFileInputRefs}
              otherFileInputRef={media.otherFileInputRef}
              onNamedFileChange={media.openNamedUpload}
              onOtherFileChange={media.openOtherUpload}
              onUploadDraftCaptionChange={media.setUploadDraftCaption}
              onConfirmUpload={media.confirmUpload}
              onCancelUpload={media.clearUploadDraft}
              onCaptionDraftChange={media.setCaptionDraft}
              onSaveCaption={media.saveCaption}
              onView={media.viewMedia}
              onRemove={media.handleRemoveMedia}
            />
            {approvalNotesDisplay ? (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Approval Notes</div>
                <Form.Control
                  as="textarea"
                  className="text-muted"
                  value={approvalNotesDisplay}
                  disabled
                  readOnly
                  style={{ overflow: "hidden", resize: "none", minHeight: "4rem" }}
                />
              </div>
            ) : null}

            {deptActionError ? <Alert variant="danger" className="mt-3 mb-0">{deptActionError}</Alert> : null}

            <div className="d-flex justify-content-end gap-2 mt-3">
              {actionsVisible && showValidate ? (
                <Button variant="outline-success" className="btn-action" onClick={handleValidateDepartmentReport} disabled={busy || !canLoad}>
                  {validatingDept ? <Spinner animation="border" size="sm" className="me-2" aria-hidden /> : null}
                  {statusName === "NEEDS_ATTENTION" ? "Re-Validate" : "Validate"}
                </Button>
              ) : null}
              {actionsVisible && showSaveAction ? (
              <Button variant="outline-primary" className="btn-action" onClick={handleSaveDraft} disabled={busy || !canLoad || !canEdit}>
                {savingDraft ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Saving…</> : "Save Draft"}
              </Button>
              ) : null}
              {actionsVisible && showSubmitAction ? (
                <Button variant="primary" className="btn-action" onClick={handleSubmitDepartmentReport} disabled={busy || !canLoad}>
                  {submittingDept ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Submitting…</> : <><FiSend className="me-2" aria-hidden />{statusName === "NEEDS_ATTENTION" ? "Resubmit" : "Submit"}</>}
                </Button>
              ) : null}
              {canRequestDepartmentReviewNow ? (
                <Button
                  variant="outline-primary"
                  className="btn-action"
                  onClick={openDepartmentReviewModal}
                  disabled={busy || !canLoad || requestingReview || approving}
                  title="Return this department report for corrections"
                >
                  <FiSend className="me-2" aria-hidden />
                  {requestReviewLabel}
                </Button>
              ) : null}
              {actionsVisible && showApprove && canApproveNow && departmentReportId ? (
                <Button variant="success" className="btn-action" onClick={() => { setApproveNotes(""); setApproveError(null); setShowApproveModal(true); }} disabled={busy || !canLoad}>
                  <FiCheckCircle className="me-2" aria-hidden />Approve Report
                </Button>
              ) : null}
              {canDownload ? (
                <Button variant="primary" className="btn-action" onClick={handleDownloadPdf} disabled={!stats || downloadingPdf} aria-busy={downloadingPdf} title="Download this report as a PDF">
                  {downloadingPdf ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Generating…</> : <><FiDownload className="me-2" aria-hidden />Download PDF</>}
                </Button>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );

  const renderBreakdown = () => (
    <>
      {compositeLoading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading financial breakdown…</span>
        </div>
      )}
      {compositeError && <Alert variant="danger" className="mb-3">{compositeError}</Alert>}
      {composite && !compositeLoading && <ChurchIncomeBreakdownTable rows={composite.income_breakdown?.rows ?? []} />}
    </>
  );

  const renderInHouse = () => (
    <>
      {compositeLoading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading district in-house memo…</span>
        </div>
      )}
      {compositeError && <Alert variant="danger" className="mb-3">{compositeError}</Alert>}
      {composite && !compositeLoading && composite.district_in_house_report ? (
        <ChurchDistrictInHouseTable block={composite.district_in_house_report} />
      ) : null}
    </>
  );

  const basePath = useEntityBasePath();
  const breadcrumbItems = React.useMemo(
    () =>
      basePath
        ? financeStatsListToViewTrail(basePath, meta?.department_name)
        : [],
    [basePath, meta?.department_name]
  );

  return (
    <Container fluid className="py-4">
      <PageBreadcrumb items={breadcrumbItems} />
      <ReportActionsPanel
        meta={{
          scopeLabel: "",
          scopeName: "",
          departmentName: meta?.department_name ?? "Finance & Statistics",
          reportTagName: meta?.report_tag_name ?? "—",
          reportTagStatusName: tagStatusName,
          statusName,
          submittedAt: submission?.submitted_at ?? null,
          submittedBy: submission?.submitted_by ?? null,
          submittedByName: submission?.submitted_by_name ?? null,
          approvedAt: submission?.approved_at ?? null,
          approvedByName: submission?.approved_by_name ?? null,
          departmentReportId,
        }}
        statusLabel="Status"
        needsAttentionReason={needsAttentionReason}
        needsAttentionDetails={needsAttentionDetails}
        statusNote={
          canEdit
            ? "This Finance & Statistics report is editable. Save Draft to keep working or Submit when it is ready for validation."
            : "This report is view-only in its current workflow state."
        }
        notices={
          <>
            {!tagIsOpen ? (
              <Alert variant="warning" className="py-2 mb-2 small">
                This reporting period is not open. Saving and submitting are unavailable.
              </Alert>
            ) : null}
            {canLoad && !departmentReportId ? <NoDepartmentReportNotice /> : null}
            {validationResult ? (
              <Alert variant={validationResult.is_valid ? "success" : "warning"} className="py-2 mb-0 small">
                {validationResult.message}
              </Alert>
            ) : null}
          </>
        }
      />

      {!canLoad && !loading && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">Select a reporting period to view the Finance &amp; Statistics report.</p>
          </Card.Body>
        </Card>
      )}

      {canLoad && (
        <FinanceStatisticsTabs
          showInHouse
          onOpen={handleTabOpen}
          renderStatistics={renderStatistics}
          renderBreakdown={renderBreakdown}
          renderInHouse={renderInHouse}
        />
      )}

      {/* Approve modal */}
      <Modal show={showApproveModal} onHide={() => { if (!approving) setShowApproveModal(false); }} centered backdrop="static">
        <Modal.Header closeButton={!approving}><Modal.Title>Approve Report</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2">Approving will move the status to <strong>{getWorkflowStatusLabel("APPROVED")}</strong>.</div>
          <Form.Group>
            <Form.Label className="fw-medium">Notes</Form.Label>
            <Form.Control as="textarea" rows={4} value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} disabled={approving} placeholder="Add approval notes (optional)..." />
          </Form.Group>
          {approveError ? <Alert variant="danger" className="mt-3 mb-0">{approveError}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowApproveModal(false)} disabled={approving}><FiX className="me-2" />Cancel</Button>
          <Button variant="primary" onClick={handleApprove} disabled={approving || !departmentReportId || !canApproveNow}>
            {approving ? <Spinner animation="border" size="sm" className="me-2" /> : <FiCheckCircle className="me-2" />}Confirm Approve
          </Button>
        </Modal.Footer>
      </Modal>

      <RequestReviewModal
        show={showReviewModal}
        onHide={() => { if (!requestingReview) setShowReviewModal(false); }}
        title={
          reviewMode === "activity"
            ? getRequestReviewCopy(currentUserId, selectedReviewTemplate?.submitted_by).actionLabel
            : requestReviewLabel
        }
        submittedBy={
          reviewMode === "activity"
            ? selectedReviewTemplate?.submitted_by
            : submission?.submitted_by
        }
        subject={
          reviewMode === "activity" ? (
            <>
              You are requesting{" "}
              {getRequestReviewCopy(currentUserId, selectedReviewTemplate?.submitted_by).noun} for:{" "}
              <span className="fw-semibold">
                {selectedReviewTemplate?.template_name ?? "Activity entry report"}
              </span>
            </>
          ) : (
            <>
              You are requesting {requestReviewNoun} for this Finance and Statistics department report. It will be marked as{" "}
              <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be edited and resubmitted.
            </>
          )
        }
        busy={requestingReview}
        error={reviewError}
        onConfirm={handleRequestReview}
      />
    </Container>
  );
}
